/**
 * Browser-Safe Schema Transforms
 * 
 * Utility functions and schemas for handling common form data transformations.
 * These are designed to work with HTML forms where:
 * - Empty strings are sent instead of undefined
 * - Select elements with no selection send empty strings
 * - Number inputs may send strings
 * 
 * CRITICAL: This file must have ZERO Node.js dependencies.
 * Only 'zod' is allowed as an import.
 * 
 * @packageDocumentation
 */

import { z } from 'zod';

// ========================================
// TRANSFORM UTILITIES
// ========================================

/**
 * Converts empty strings to undefined.
 * Use this for optional string fields in forms.
 * 
 * @example
 * ```typescript
 * const schema = z.object({
 *   nickname: emptyToUndefined.optional(),
 * });
 * // { nickname: '' } → { nickname: undefined }
 * // { nickname: 'Bob' } → { nickname: 'Bob' }
 * ```
 */
export const emptyToUndefined = z.string().transform(v => v === '' ? undefined : v);

/**
 * Converts empty strings to null.
 * Use this for nullable string fields that need to explicitly store null.
 * 
 * @example
 * ```typescript
 * const schema = z.object({
 *   deletedAt: emptyToNull.nullable(),
 * });
 * ```
 */
export const emptyToNull = z.string().transform(v => v === '' ? null : v);

/**
 * Creates an optional enum that converts empty strings to undefined.
 * Perfect for <select> elements with an empty "Select..." option.
 * 
 * @example
 * ```typescript
 * const schema = z.object({
 *   transport: optionalEnum(['UDP', 'TCP', 'TLS']),
 *   // <select value="">Select...</select> → undefined
 *   // <select value="TLS">TLS</select> → 'TLS'
 * });
 * ```
 */
export function optionalEnum<T extends readonly [string, ...string[]]>(values: T) {
  return z.union([
    z.enum(values),
    z.literal('').transform(() => undefined as unknown as T[number] | undefined)
  ]).optional();
}

/**
 * Creates a nullable enum that converts empty strings to null.
 * Use when the field can be explicitly null in the database.
 * 
 * @example
 * ```typescript
 * const schema = z.object({
 *   algorithm: nullableEnum(['MD5', 'SHA-256']),
 * });
 * ```
 */
export function nullableEnum<T extends readonly [string, ...string[]]>(values: T) {
  return z.union([
    z.enum(values),
    z.literal('').transform(() => null as unknown as T[number] | null)
  ]).nullable();
}

/**
 * Converts string to number, with empty string becoming undefined.
 * Perfect for number inputs that may be empty.
 * 
 * Handles:
 * - Empty string "" → undefined
 * - NaN (from valueAsNumber on empty input) → undefined
 * - Valid number → number
 * 
 * @example
 * ```typescript
 * const schema = z.object({
 *   maxCalls: optionalNumber,
 * });
 * // { maxCalls: '' } → { maxCalls: undefined }
 * // { maxCalls: '100' } → { maxCalls: 100 }
 * // { maxCalls: NaN } → { maxCalls: undefined }
 * ```
 */
export const optionalNumber = z.preprocess(
  (val) => {
    // Handle NaN from valueAsNumber on empty inputs
    if (typeof val === 'number' && isNaN(val)) return undefined;
    // Handle empty string
    if (val === '' || val === null || val === undefined) return undefined;
    // Convert string to number
    if (typeof val === 'string') {
      const num = Number(val);
      return isNaN(num) ? undefined : num;
    }
    return val;
  },
  z.union([z.number(), z.undefined()])
).optional();

/**
 * Converts string to number with validation, empty becomes undefined.
 * Use when you need min/max constraints.
 * 
 * Handles:
 * - Empty string "" → undefined
 * - NaN (from valueAsNumber on empty input) → undefined
 * - Valid number within range → number
 * - Number outside range → undefined
 * 
 * @example
 * ```typescript
 * const schema = z.object({
 *   port: optionalNumberInRange(1, 65535),
 * });
 * ```
 */
export function optionalNumberInRange(min: number, max: number) {
  return z.preprocess(
    (val) => {
      // Handle NaN from valueAsNumber on empty inputs
      if (typeof val === 'number' && isNaN(val)) return undefined;
      // Handle empty string
      if (val === '' || val === null || val === undefined) return undefined;
      return val;
    },
    z.union([
      z.number().min(min).max(max),
      z.undefined()
    ])
  ).optional();
}

/**
 * Optional UUID that converts empty strings from <select> elements to undefined.
 * Use for optional foreign key fields in forms.
 * Uses z.union (not z.preprocess) so z.input infers optional fields correctly.
 *
 * @example
 * ```typescript
 * const schema = z.object({
 *   sipInterfaceId: optionalNullableUuid,
 * });
 * // <select value="">None</select> → undefined
 * // <select value="uuid-here">Interface</select> → validated UUID
 * ```
 */
export const optionalNullableUuid = z
  .union([
    z.string().uuid(),
    z.literal('').transform(() => undefined),
    z.null(),
    z.undefined(),
  ])
  .optional();

/**
 * Creates an optional string with min/max length that converts empty to undefined.
 * 
 * @example
 * ```typescript
 * const schema = z.object({
 *   username: optionalString(1, 100),
 * });
 * ```
 */
export function optionalString(minLength?: number, maxLength?: number) {
  let schema = z.string();
  if (minLength !== undefined) schema = schema.min(minLength);
  if (maxLength !== undefined) schema = schema.max(maxLength);
  
  return z.union([
    schema,
    z.literal('').transform(() => undefined)
  ]).optional();
}

/**
 * Preprocessor that recursively cleans form data:
 * - Converts empty strings to undefined
 * - Removes null values (converts to undefined)
 * - Removes empty objects
 * 
 * Use with z.preprocess() for comprehensive form data cleaning.
 * 
 * @example
 * ```typescript
 * const schema = z.preprocess(cleanFormData, z.object({
 *   name: z.string(),
 *   config: z.object({ ... }).optional(),
 * }));
 * ```
 */
export function cleanFormData(data: unknown): unknown {
  if (data === null || data === '') {
    return undefined;
  }
  
  if (Array.isArray(data)) {
    const cleaned = data
      .map(item => cleanFormData(item))
      .filter(item => item !== undefined);
    return cleaned.length > 0 ? cleaned : undefined;
  }
  
  if (typeof data === 'object' && data !== null) {
    const cleaned: Record<string, unknown> = {};
    let hasValues = false;
    
    for (const [key, value] of Object.entries(data)) {
      const cleanedValue = cleanFormData(value);
      if (cleanedValue !== undefined) {
        cleaned[key] = cleanedValue;
        hasValues = true;
      }
    }
    
    return hasValues ? cleaned : undefined;
  }
  
  return data;
}

/**
 * Creates a schema with preprocessing that cleans form data.
 * Wraps any schema with the cleanFormData preprocessor.
 * 
 * @example
 * ```typescript
 * const PeerSchema = withFormPreprocessing(z.object({
 *   name: z.string(),
 *   tlsConfig: z.object({
 *     minVersion: z.enum(['TLSv1.2', 'TLSv1.3']).optional(),
 *   }).optional(),
 * }));
 * // Form: { tlsConfig: { minVersion: '' } }
 * // Result: { tlsConfig: undefined } (empty nested object removed)
 * ```
 */
export function withFormPreprocessing<T extends z.ZodTypeAny>(schema: T) {
  return z.preprocess(cleanFormData, schema);
}
